import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { API_CONFIG } from '@/constants/env';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ClipboardList, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { GlowBackground } from "@/components/GlowBackground.tsx";
import { SuccessModal } from "@/components/SuccessModal";
import { collectBrowserInfo, type BrowserInfo } from "@/utils/browserInfo";
import PageViewTracker from '@/components/analytics/PageViewTracker';
import { trackEvent, trackTiming } from '@/utils/analytics';

const formSchema = (t: (key: string) => string) =>
  z.object({
    first_name: z
      .string()
      .min(1, t('signup.validation.firstNameRequired') || 'First name is required'),
    last_name: z
      .string()
      .min(1, t('signup.validation.lastNameRequired') || 'Last name is required'),
    email: z.string().email(t('signup.validation.invalidEmail') || 'Invalid email address'),
    company: z
      .string()
      .min(1, t('signup.validation.companyRequired') || 'Company name is required'),
    industry: z.string().min(1, t('signup.validation.industryRequired') || 'Industry is required'),
    company_size: z
      .string()
      .min(1, t('signup.validation.companySizeRequired') || 'Company size is required'),
    compliance_needs: z
      .string()
      .min(1, t('signup.validation.complianceNeedsRequired') || 'Compliance needs are required'),
    process_requirements: z
      .string()
      .min(
        1,
        t('signup.validation.processRequirementsRequired') || 'Process requirements are required'
      ),
    additional_info: z.string().optional(),
  });

// Use a type cast to make TypeScript happy with our parameterized schema
type FormData = z.infer<ReturnType<typeof formSchema>>;

const SignUpPage = () => {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language;
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Create the validation schema with translations
  const validationSchema = formSchema(t);

  useEffect(() => {
    // Set document title
    document.title = t('signup.pageTitle') || 'Sign Up - Verdea AI';

    // Check if running on localhost
    const hostname = window.location.hostname;
    setIsLocalhost(hostname === 'localhost' || hostname === '127.0.0.1');
  }, [t]);

  const form = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      company: '',
      industry: '',
      company_size: '',
      compliance_needs: '',
      process_requirements: '',
      additional_info: '',
    },
  });

  // Set fake data for development environment
  useEffect(() => {
    if (isLocalhost) {
      form.reset({
        first_name: 'Jane',
        last_name: 'Smith',
        email: 'jane.smith@example.com',
        company: 'Compliance Co.',
        industry: 'Healthcare',
        company_size: '100-500',
        compliance_needs: 'ESG Reporting, GDPR Compliance',
        process_requirements:
          'Need to automate our regulatory reporting process and improve data quality checks.',
        additional_info: "We're looking to implement a solution within the next quarter.",
      });
    }
  }, [isLocalhost, form]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    // Collect browser and device information
    const browserInfo = collectBrowserInfo();
    
    // Prepare submission data with browser info
    const submissionData = {
      ...data,
      browser_info: browserInfo
    };
    
    // Track the form submission attempt
    trackEvent('SignUp', 'FormSubmit', 'SignupForm', {
      industry: data.industry,
      companySize: data.company_size
    });
    
    // Start timing for form submission
    const startTime = performance.now();
    
    try {
      const response = await fetch(`${API_CONFIG.API_BASE_URL}/signup/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Track successful form submission
      trackEvent('SignUp', 'FormSubmitSuccess', 'SignupForm');

      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      let errorMessage = t('signup.notifications.error.description');

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: t('signup.notifications.error.title'),
        description: errorMessage,
        variant: 'destructive',
      });
      
      // Track form submission error
      trackEvent('SignUp', 'FormSubmitError', errorMessage);
      
      setIsSubmitting(false);
    } finally {
      // Track timing for form submission
      const endTime = performance.now();
      const timeTaken = Math.round(endTime - startTime);
      trackTiming('SignUp', 'FormSubmission', timeTaken, 'SignupForm');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PageViewTracker path="/signup" title="Sign Up for Verdea - Start Your Compliance Journey" />
      <NavBar />
      <GlowBackground colorScheme={'green'} />

      <div className="container mx-auto px-4 pt-24 pb-16 flex-grow relative z-10">
        {/* Background Elements - Apple style with green theme */}
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          {/* Primary gradient blur - Green theme */}
          <div
            className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
            style={{
              background:
                'radial-gradient(circle at center, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0) 70%)', // Green-600
              transform: 'translate(20%, -20%)',
            }}
          />

          {/* Secondary gradient blur */}
          <div
            className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
            style={{
              background:
                'radial-gradient(circle at center, rgba(110, 231, 183, 0.3) 0%, rgba(110, 231, 183, 0) 70%)', // Green-300
              transform: 'translate(-20%, 20%)',
            }}
          />

          {/* Subtle dot grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: 'radial-gradient(rgba(16, 185, 129, 0.2) 1px, transparent 1px)', // Green-600
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="mb-12 text-center">
            <Badge
              variant="outline"
              className="px-4 py-1.5 mb-6 bg-green-50 border-green-100 text-green-800 text-sm font-medium dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400"
            >
              {t('signup.badge')}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
              {t('signup.heading.part1')}<br/>
              <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent font-semibold">
                {t('signup.heading.highlight')}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('signup.subheading')}
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-8 ${showSuccessModal ? 'pointer-events-none opacity-75' : ''}`}>
              <div className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                  <User className="h-5 w-5 text-green-600" />
                  {t('signup.personalInfo.title')}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('signup.personalInfo.firstName')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('signup.personalInfo.firstNamePlaceholder')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('signup.personalInfo.lastName')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('signup.personalInfo.lastNamePlaceholder')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('signup.personalInfo.email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t('signup.personalInfo.emailPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('signup.personalInfo.company')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('signup.personalInfo.companyPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="industry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('signup.personalInfo.industry')}</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            field.onChange(value);
                            trackEvent('SignUp', 'IndustrySelect', value);
                          }} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t('signup.personalInfo.industryPlaceholder')}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="banking">
                              {t('signup.industries.banking')}
                            </SelectItem>
                            <SelectItem value="healthcare">
                              {t('signup.industries.healthcare')}
                            </SelectItem>
                            <SelectItem value="insurance">
                              {t('signup.industries.insurance')}
                            </SelectItem>
                            <SelectItem value="manufacturing">
                              {t('signup.industries.manufacturing')}
                            </SelectItem>
                            <SelectItem value="retail">{t('signup.industries.retail')}</SelectItem>
                            <SelectItem value="technology">
                              {t('signup.industries.technology')}
                            </SelectItem>
                            <SelectItem value="energy">{t('signup.industries.energy')}</SelectItem>
                            <SelectItem value="real_estate">
                              {t('signup.industries.realEstate')}
                            </SelectItem>
                            <SelectItem value="other">{t('signup.industries.other')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="company_size"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('signup.personalInfo.companySize')}</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            field.onChange(value);
                            trackEvent('SignUp', 'CompanySizeSelect', value);
                          }} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t('signup.personalInfo.companySizePlaceholder')}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1-50">{t('signup.companySizes.small')}</SelectItem>
                            <SelectItem value="51-200">
                              {t('signup.companySizes.medium')}
                            </SelectItem>
                            <SelectItem value="201-500">
                              {t('signup.companySizes.mediumLarge')}
                            </SelectItem>
                            <SelectItem value="501-1000">
                              {t('signup.companySizes.large')}
                            </SelectItem>
                            <SelectItem value="1001-5000">
                              {t('signup.companySizes.veryLarge')}
                            </SelectItem>
                            <SelectItem value="5000+">
                              {t('signup.companySizes.enterprise')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                  <ClipboardList className="h-5 w-5 text-green-600" />
                  {t('signup.complianceRequirements.title')}
                </h2>
                <FormField
                  control={form.control}
                  name="compliance_needs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('signup.complianceRequirements.complianceNeeds')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                'signup.complianceRequirements.complianceNeedsPlaceholder'
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="esg_reporting">
                            {t('signup.complianceTypes.esg')}
                          </SelectItem>
                          <SelectItem value="gdpr">{t('signup.complianceTypes.gdpr')}</SelectItem>
                          <SelectItem value="financial_regulatory">
                            {t('signup.complianceTypes.financial')}
                          </SelectItem>
                          <SelectItem value="industry_standards">
                            {t('signup.complianceTypes.industry')}
                          </SelectItem>
                          <SelectItem value="data_privacy">
                            {t('signup.complianceTypes.privacy')}
                          </SelectItem>
                          <SelectItem value="risk_management">
                            {t('signup.complianceTypes.risk')}
                          </SelectItem>
                          <SelectItem value="multiple">
                            {t('signup.complianceTypes.multiple')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="process_requirements"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('signup.complianceRequirements.processRequirements')}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t(
                            'signup.complianceRequirements.processRequirementsPlaceholder'
                          )}
                          className="min-h-[120px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="additional_info"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('signup.complianceRequirements.additionalInfo')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('signup.complianceRequirements.additionalInfoPlaceholder')}
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col items-center space-y-4">
                <Button
                  type="submit"
                  className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white px-8"
                  size="lg"
                  disabled={isSubmitting || showSuccessModal}
                  onClick={() => trackEvent('SignUp', 'ButtonClick', 'SubmitButton')}
                >
                  {isSubmitting ? t('signup.button.submitting') : t('signup.button.submit')}
                </Button>
                <p className="text-sm text-slate-500 max-w-lg mt-16 text-center">
                  {t('signup.termsNotice')}{' '}
                  <a
                    href="/terms"
                    className="text-green-600 hover:underline"
                    onClick={() => trackEvent('SignUp', 'LinkClick', 'Terms of Service')}
                  >
                    {t('signup.termsLink')}
                  </a>{' '}
                  {t('signup.andText')}{' '}
                  <a
                    href="/privacy"
                    className="text-green-600 hover:underline"
                    onClick={() => trackEvent('SignUp', 'LinkClick', 'Privacy Policy')}
                  >
                    {t('signup.privacyLink')}
                  </a>
                  .
                </p>
              </div>
            </form>
          </Form>
          
          {/* Success Modal */}
          <SuccessModal
            isOpen={showSuccessModal}
            onClose={() => {
              setShowSuccessModal(false);
              form.reset();
            }}
            title={t('signup.notifications.success.title')}
            description={t('signup.notifications.success.description')}
          />
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default SignUpPage;
